"use client";

import Badge from "@/components/atoms/badge/Badge";
import ErrorState from "@/components/atoms/errorState/ErrorState";
import EyeCloseIcon from "@/components/atoms/svg/icons/EyeCloseIcon";
import EyeIcon from "@/components/atoms/svg/icons/EyeIcon";
import RefreshIcon from "@/components/atoms/svg/icons/RefreshIcon";
import { Input } from "@/components/ui/input";
import { FORM_SCHEMA_REGISTER } from "@/constants/regex";
import useRegister from "@/features/authentication/hooks/useRegister";
import { zodResolver } from "@hookform/resolvers/zod";
import clsx from "clsx";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { z } from "zod";

type FormResetPasswordValues = z.infer<typeof FORM_SCHEMA_REGISTER>;

const defaultValues = {
  email: "",
  password: "",
  confirmPassword: "",
  otp_number: "",
};

const FormRegister = () => {
  const {
    onGetCode,
    onSubmit,
    isDisabledSubmit,
    // TODO: Uncomment this line when function is completed
    isDisabledGetCode,
    showPassword,
    showConfirmPassword,
    setShowPassword,
    setShowConfirmPassword,
  } = useRegister();

  const form = useForm<FormResetPasswordValues>({
    defaultValues,
    mode: "onChange",
    resolver: zodResolver(FORM_SCHEMA_REGISTER),
  });

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4 min-w-[352px] max-w-[352px]"
      >
        {/* Email */}
        <Controller
          control={form.control}
          name="email"
          render={({ field }) => (
            <div className="relative">
              <Input
                type="email"
                value={field.value}
                onChange={field.onChange}
                placeholder="Email"
                className="base-input"
              />
              {form.formState.errors.email && (
                <ErrorState message={form.formState.errors.email.message} />
              )}
            </div>
          )}
        />
        {/* GET Code OTP */}
        <Controller
          control={form.control}
          name="otp_number"
          render={({ field }) => (
            <div className="space-y-2">
              <div className="relative flex items-center justify-between">
                <NumericFormat
                  {...field}
                  className="base-input"
                  allowNegative={false}
                  // Set this to true to allow leading zeros
                  allowLeadingZeros={true}
                  decimalScale={0}
                  placeholder="OTP"
                  onValueChange={(v) => field.onChange(v.value)}
                  customInput={Input}
                />
                <button
                  type="button"
                  onClick={(e) => onGetCode(form.getValues().email, e)}
                  className={clsx(
                    "absolute right-2 disabled:opacity-40",
                    // TODO: Uncomment this line when function is completed
                    !!form.watch("email") ||
                      form.getFieldState("email").invalid ||
                      isDisabledGetCode,
                  )}
                  // TODO: Uncomment this line when function is completed
                  disabled={
                    !form.watch("email") ||
                    form.getFieldState("email").invalid ||
                    isDisabledGetCode
                  }
                >
                  <Badge className="w-[82px] h-[28px]">Send OTP</Badge>
                </button>
              </div>
              {form.formState.errors.otp_number && (
                <ErrorState
                  message={form.formState.errors.otp_number.message}
                />
              )}
            </div>
          )}
        />
        {/* Password */}
        <Controller
          control={form.control}
          name="password"
          render={({ field }) => (
            <div className="space-y-2">
              <div className="relative flex items-center justify-between">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Password"
                  className="base-input"
                />
                <button
                  type="button"
                  aria-label="Show password"
                  className="absolute right-2 opacity-40"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeCloseIcon /> : <EyeIcon />}
                </button>
              </div>
              {form.formState.errors.password && (
                <ErrorState message={form.formState.errors.password.message} />
              )}
            </div>
          )}
        />
        {/* Confirm Password */}
        <Controller
          control={form.control}
          name="confirmPassword"
          render={({ field }) => (
            <div className="space-y-2">
              <div className="relative flex items-center justify-between">
                <Input
                  type={showConfirmPassword ? "text" : "password"}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Confirm Password"
                  className="base-input"
                />
                <button
                  type="button"
                  aria-label="Show password"
                  className="absolute right-2 opacity-40"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeCloseIcon /> : <EyeIcon />}
                </button>
              </div>
              {form.formState.errors.confirmPassword && (
                <ErrorState
                  message={form.formState.errors.confirmPassword.message}
                />
              )}
            </div>
          )}
        />
        <button
          type="submit"
          className={clsx(
            "btn-purple btn-large flex-1 w-full !text-white",
            isDisabledSubmit &&
              "flex items-center justify-center gap-4 btn-large/40",
          )}
          disabled={isDisabledSubmit}
        >
          Sign up
          {isDisabledSubmit && (
            <RefreshIcon className="animate-spin" fillOpacity={0.6} />
          )}
        </button>
      </form>
    </FormProvider>
  );
};

export default FormRegister;
